import { CommonColumnProperties, CustomColumns, CustomFilter, ImportWizard, ImportWizardRef, ImportWizardSetting, ProTable, Title } from "components";
import { ProcessingState, ValidationState } from "components/ImportWizard/ImportWizardTypes";
import { runBatchWorkers } from "components/WebWorkers/ZWebWorker";
import { ZCheckboxButton } from "components/ZCheckboxButton";
import { ZIcon } from "components/ZIcon";
import { ZLabel } from "components/ZLabel";
import { ZMultiSelectDropdown } from "components/ZMultiSelectDropdown";
import { ZTooltip } from "components/ZTooltip";
import { onlyUnique, useDisplayError } from "helpers/Functions";
import { ParseAndValidateFloat, ToFixedNotRounded, ValidateBoolean, ValidateInt, getRowIds } from "helpers/data/dataHandling";
import { LoadFilter, SaveFilter } from "helpers/filterStorage";
import { ZIconType } from "helpers/utility/iconMap";
import { useProductFilter, useStockFilter } from "hooks";
import { SystemStocksPopup } from "popups/SystemStocks/SystemStocks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Dropdown, Form, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getDepartments } from "redux/slices/Departments";
import { importFromProducts } from "redux/slices/Products";
import { createStockGroup, getStockGroups } from "redux/slices/StockGroups";
import {
	addDeselectedRows,
	addSelectedRows,
	cleanStockDetails,
	clearFilters,
	createStock,
	deleteStocks,
	getStockTable,
	importFromProductsWithData,
	updateMultipleStocks,
	updateStockField,
} from "redux/slices/Stocks";
import { getSubDepartments } from "redux/slices/SubDepartment";
import { getSuppliers } from "redux/slices/Suppliers";
import { RootState } from "redux/store";
import { ManagedStockMerge } from "../Managed/ManagedStockMerge";
import { BulkEdit } from "../Managed/Popups/BulkEdit/BulkEdit";
import { ManagedStockSync } from "../Managed/Popups/ManagedStockSync";
import { StockDetailImportWorkerData, StockDetailImportWorkerResponseData } from "../Managed/stockDetailsImportWorker";
import { SystemStocksImportWorkerData, SystemStocksImportWorkerResponseData } from "../Managed/systemStocksImportWorker";
import { ZFeatureFlag } from "components/ZFeatureFlag";
import { AgGridReact, AgGridReactProps, CustomCellRendererProps } from "ag-grid-react";
import { ManagedStockView } from "models/dbModels/managedStock/ManagedStock";
import { IServerSideDatasource } from "ag-grid-enterprise";
import { AdvancedFilterModel, ColDef, ColGroupDef, ColumnState, FilterModel, GridReadyEvent, GridState, IServerSideGroupSelectionState, IServerSideSelectionState } from "ag-grid-community";
import { Z } from "models/common/components";
import BaseCellRenderer from "components/AgGrid/CellRenderers/BaseCellRenderer";
import { AgGridToolbar, AgGridToolbarRef } from "components/AgGrid/AgGridToolbar";
import { FilterState, createBooleanFilter, createBooleanSelectFilter, createSingleSelectFilter } from "components/AgGrid/FilterComponents/Filters";
import { ManagedDepartment } from "models/dbModels/managedDepartments/ManagedDepartment";
import { ManagedSupplier } from "models/dbModels/managedSuppliers/ManagedSupplier";
import { ManagedSubdepartment } from "models/dbModels/managedSubdepartments/ManagedSubdepartment";
import { ManagedStockGroup } from "models/dbModels/managedStockGroups/ManagedStockGroup";
import { ScheduleCodes } from "constants/constants";
import { SearchState } from "components/AgGrid/FilterComponents/Search";
import { ZButton } from "components/ZButton";
import { ZSingleSelectDropdown } from "components/ZSingleSelectDropdown";
import ts from "typescript";
import { ManagedStockSearchFilterQuery } from "models/filters/managedStocks/ManagedStockSearchFilterQuery";
import PriceCellEditor from "components/AgGrid/CellEditors/PriceCellEditor";
import { getManufacturers } from "redux/slices/ManagedManufacturers";
import { ProductImport, ProductImportExtended } from "models/dbModels/managedStock/OptionalManagedStockUpdate";

const importWizardExtraMatches: CustomColumns[] = [
	{
		title: "Name",
		key: "name",
		required: false,
		display: true,
		autoMatch: ["name", "description", "stock name"],
		validation: (value: any) => ({ valid: true, error: "" }),
	},
	{
		title: "Preferred Supplier",
		key: "supplier",
		required: false,
		display: true,
		autoMatch: ["prefsupplier"],
		validation: (value: any) => ({ valid: true, error: "" }),
	},
	{
		title: "Department",
		key: "department",
		required: false,
		display: true,
		autoMatch: ["department", "dept"],
		validation: (value: any) => ({ valid: true, error: "" }),
	},
	{
		title: "Sub Department",
		key: "subdepartment",
		required: false,
		display: true,
		autoMatch: ["sub department", "sub dept", "subdepartment", "subdept"],
		validation: (value: any) => ({ valid: true, error: "" }),
	},
	{
		title: "Manufacturer",
		key: "manufacturer",
		required: false,
		display: true,
		autoMatch: ["manufacturer", "manuf", "manu"],
		validation: (value: any) => ({ valid: true, error: "" }),
	},
	{
		title: "Min Order Qty",
		key: "minOrderQty",
		required: false,
		display: true,
		autoMatch: ["min order qty", "minorderqty"],
		validation: (value: any) => {
			if (value == null || value == "") {
				return { valid: true, error: "" };
			}

			return ValidateInt(value, 0) ? { valid: true, error: "" } : { valid: false, error: "Min order qty invalid" };
		},
	},
	{
		title: "Max Stock Level",
		key: "maxStockLevel",
		required: false,
		display: true,
		autoMatch: ["max stock level", "max stock"],
		validation: (value: any) => {
			if (value == null || value == "") {
				return { valid: true, error: "" };
			}

			return ValidateInt(value, 0) ? { valid: true, error: "" } : { valid: false, error: "Max stock level invalid" };
		},
	},
	{
		title: "Min Stock Level",
		key: "minStockLevel",
		required: false,
		display: true,
		autoMatch: ["min stock level", "min stock"],
		validation: (value: any) => {
			if (value == null || value == "") {
				return { valid: true, error: "" };
			}

			return ValidateInt(value, 0) ? { valid: true, error: "" } : { valid: false, error: "Min stock level invalid" };
		},
	},
	{
		title: "Carton Size",
		key: "cartonSize",
		required: false,
		display: true,
		autoMatch: ["carton size"],
		validation: (value: any) => {
			if (value == null || value == "") {
				return { valid: true, error: "" };
			}

			return ValidateInt(value, 1) ? { valid: true, error: "" } : { valid: false, error: "Carton size invalid" };
		},
	},
	{
		title: "Cost",
		key: "costEx",
		required: false,
		display: true,
		autoMatch: ["cost", "cost ex", "costex"],
		validation: (value: any) => {
			if (value == null || value === "") {
				return { valid: true, error: "" };
			}

			let res = ParseAndValidateFloat(value, 0);
			if (res.output) {
				return { valid: true, error: "", newValue: ToFixedNotRounded(res.value, 2) };
			} else {
				return { valid: false, error: "Cost invalid" };
			}
		},
	},
	{
		title: "GST",
		key: "gstCode",
		required: false,
		display: true,
		autoMatch: ["gst"],
		validation: (value: any) => {
			if (value == null || value === "") {
				return { valid: true, error: "" };
			}

			if (value?.toLocaleLowerCase() === "y" || value?.toLocaleLowerCase() === "n" || value?.toLocaleLowerCase() === "f") {
				return { valid: true, error: "", newValue: value?.toLocaleUpperCase() };
			} else {
				return { valid: false, error: "Invalid GST, must be Y, N or F" };
			}
		},
	},
	{
		title: "Sell Price",
		key: "baseSellPrice",
		required: false,
		display: true,
		autoMatch: ["sell", "sell price", "retail"],
		validation: (value: any) => {
			if (value === "" || value == null) {
				return { valid: true, error: "" };
			}

			let res = ParseAndValidateFloat(value, 0);
			if (res.output) {
				return { valid: true, error: "", newValue: ToFixedNotRounded(res.value, 2) };
			} else {
				return { valid: false, error: "Sell price invalid" };
			}
		},
	},
	{
		title: "RRP",
		key: "rrp",
		required: false,
		display: true,
		autoMatch: ["rrp"],
		validation: (value: any) => {
			if (value === "" || value == null) {
				return { valid: true, error: "", newValue: value };
			}

			let res = ParseAndValidateFloat(value, 0);
			if (res.output) {
				return { valid: true, error: "", newValue: ToFixedNotRounded(res.value, 2) };
			} else {
				return { valid: false, error: "RRP invalid" };
			}
		},
	},
	{
		title: "Active",
		key: "active",
		required: false,
		display: true,
		autoMatch: ["active"],
		validation: (value: any) => {
			let result = ValidateBoolean(value);
			return { valid: result.valid, error: result.valid ? "" : "Active value invalid", newValue: result.newValue };
		},
	},
	{
		title: "Ethical",
		key: "ethical",
		required: false,
		display: true,
		autoMatch: ["ethical"],
		validation: (value: any) => {
			let result = ValidateBoolean(value);
			return { valid: result.valid, error: result.valid ? "" : "Ethical value invalid", newValue: result.newValue };
		},
	},
	{
		title: "Stock Groups",
		key: "stockGroup",
		required: false,
		display: true,
		autoMatch: ["stock groups", "stock group", "stockgroups", "stockgroup", "groups", "group"],
		validation: (value: any) => ({ valid: true, error: "" }),
	},
	// {
	// 	title: "Locked",
	// 	key: "locked",
	// 	required: false,
	// 	display: true,
	// 	autoMatch: ["locked"],
	// 	validation: (value: any) => {
	// 		let result = ValidateBoolean(value);
	// 		return ({ valid: result.valid, error: result.valid ? "" : "Locked value invalid", newValue: result.newValue });
	// 	}
	// },
	{
		title: "Additional Order Info",
		key: "additionalInfo",
		required: false,
		display: true,
		autoMatch: ["additional order info", "order info", "additional"],
		validation: (value: any) => ({ valid: true, error: "" }),
	},
];

export function ManagedStocks() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const displayError = useDisplayError();
	const getStockFilterOptions = useStockFilter();
	const getProductFilterOptions = useProductFilter();
	const [syncOpen, setSyncOpen] = useState<boolean>(false);
	const stocks = useAppSelector((state: RootState) => state.stocks);
	const products = useAppSelector((state: RootState) => state.products);
	const departments = useAppSelector((state: RootState) => state.department);
	const subDepartments = useAppSelector((state: RootState) => state.subDepartment);
	const suppliers = useAppSelector((state: RootState) => state.suppliers);
	const stockGroups = useAppSelector((state: RootState) => state.stockGroups);
	const global = useAppSelector((state: RootState) => state.global);
	const manufacturers = useAppSelector((state: RootState) => state.managedManufacturers);
	const [importPercentage, setImportPercentage] = useState<number>(0);
	const [importLabel, setImportLabel] = useState<string | undefined>(undefined);
	const [counterSelectedRows, setCounterSelectedRows] = useState<number>(0);
	const [showSystemStocksPopup, setShowSystemStocksPopup] = useState<boolean>(false);
	const user = useAppSelector((state: RootState) => state.logedinUser);
	const importWizardRef = useRef<ImportWizardRef>(null);

	const columns = useMemo(
		() => [
			{
				Header: "Name",
				accessor: "name",
				defaultCanSort: true,
				width: 1000,
				Cell: (row: any) => (user.permissions.managedStock.canUpdate ? <Link to={`/app/stock/${row.cell.row.original.id}`}>{row.value}</Link> : <>{row.value}</>),
			},
			{
				Header: "Ethical",
				accessor: "ethical",
				width: 400,
				Cell: (row: any) => {
					return (
						<>
							{!row.cell.row.original.ethical && <span>No</span>}
							{row.cell.row.original.ethical === true && <span>Yes</span>}
						</>
					);
				},
			},
			{
				Header: "Active",
				accessor: "active",
				width: 400,
				Cell: (row: any) => {
					return (
						<>
							{row.cell.row.original.active === false && <span>No</span>}
							{row.cell.row.original.active === true && <span>Yes</span>}
						</>
					);
				},
			},
			{
				Header: "Z ID",
				accessor: "productNo",
				width: 400,
				defaultCanSort: true,
				Cell: (row: any) => {
					return <>{row.cell.row.original.productNo}</>;
				},
			},
		],
		[stocks.table]
	);

	useEffect(() => {
		dispatch(cleanStockDetails());
		dispatch(clearFilters());
		dispatch(getStockTable({}));
		LoadFilter<{ searchPdes: Boolean; searchBarcodes: Boolean }>("StockSearchFilter").then((previousFilter) => {
			if (previousFilter) {
				dispatch(updateStockField({ key: "searchPdes", value: previousFilter.searchPdes }));
				dispatch(updateStockField({ key: "searchBarcodes", value: previousFilter.searchBarcodes }));
			}
		});
	}, []);

	function saveStockSearchFilter() {
		SaveFilter("StockSearchFilter", { searchPdes: stocks.searchPdes, searchBarcodes: stocks.searchBarcodes });
	}

	useEffect(() => {
		let rows = 0;
		if (!stocks.selectedRows) {
			setCounterSelectedRows(0);
		}

		if (stocks.selectAllFilter) {
			let deselectedRows = 0;
			stocks.deselectedRows.forEach((row: any) => (deselectedRows = deselectedRows + row.length));
			rows = stocks.totalRows - deselectedRows;
		} else if (stocks.selectedRows.some((r) => (r as any).length !== 0)) {
			stocks.selectedRows.forEach((row: any) => (rows = rows + row.length));
		} else {
			rows = stocks.totalRows;
		}

		setCounterSelectedRows(rows);
		const selecteRowsIds: number[] = [];
		stocks.selectedRows.forEach((rowIndex: any) => rowIndex.forEach((row: any) => selecteRowsIds.push(row.id)));
		const deselectedRowsIds: number[] = [];
		stocks.deselectedRows.forEach((rowIndex: any) => rowIndex.forEach((row: any) => deselectedRowsIds.push(row.id)));
	}, [stocks.selectedRows, stocks.deselectedRows, stocks.totalRows]);

	useEffect(() => {
		columnCheckDeselectAll();
	}, [stocks.pageSize]);

	function handleChange(option: any, optionValue: any) {
		dispatch(updateStockField({ key: option, value: optionValue }));
	}

	function handleDeleteStock(deleteCallback: () => void) {
		dispatch(
			deleteStocks({
				...getStockFilterOptions(),
				ids: stocks.selectAllFilter ? [] : getRowIds(stocks.selectedRows),
				excludedIds: stocks.selectAllFilter ? getRowIds(stocks.deselectedRows) : [],
				searchPdes: stocks.searchPdes,
				searchBarcodes: stocks.searchBarcodes,
			})
		).then((res) => {
			deleteCallback();
			if (res.data) {
				columnCheckDeselectAll();
				dispatch(clearFilters());
				dispatch(
					getStockTable({
						...getStockFilterOptions(),
					})
				);
				stocks.totalPages - 1 === stocks.pageIndex && handleChange("pageIndex", 0);
			}
		});
		saveStockSearchFilter();
	}

	function onSearchClick(keyword: string) {
		dispatch(updateStockField({ key: "keyword", value: keyword }));
		dispatch(updateStockField({ key: "pageIndex", value: 0 }));
		columnCheckDeselectAll();
		dispatch(
			getStockTable({
				...getStockFilterOptions(),
				keyword: keyword,
				searchPdes: stocks.searchPdes,
				searchBarcodes: stocks.searchBarcodes,
				limit: stocks.pageSize,
			})
		);

		saveStockSearchFilter();
	}

	function updateTableRows(dataTableState: any) {
		dispatch(updateStockField({ key: "pageIndex", value: dataTableState.pageIndex }));
		dispatch(updateStockField({ key: "pageSize", value: dataTableState.pageSize }));
		dispatch(
			getStockTable({
				...getStockFilterOptions(),
				keyword: stocks.keyword,
				offset: dataTableState.pageIndex * dataTableState.pageSize,
				limit: dataTableState.pageSize,
			})
		);
		saveStockSearchFilter();
	}

	function selectedRowsUpdate(rows: any, index: any) {
		dispatch(addSelectedRows({ rows: rows, index: index }));
	}

	function deselectedRowsUpdate(rows: any, index: any) {
		dispatch(addDeselectedRows({ rows: rows, index: index }));
	}

	function handleFilterChange(option: any, optionValue: any) {
		dispatch(updateStockField({ key: option, value: optionValue }));
	}

	function columnCheckDeselectAll() {
		handleChange("selectAllFilter", false);
		handleChange("deselectedRows", []);
		handleChange("selectedRows", []);
	}

	function columnCheckSelectAll() {
		handleChange("selectAllFilter", true);
		handleChange("deselectedRows", []);
	}

	function saveSelectedSystemStocks() {
		dispatch(
			importFromProducts({
				filter: {
					...getProductFilterOptions(),
					searchPdes: products.searchPdes,
					searchBarcodes: products.searchBarcodes,
					ids: products.selectAllFilter ? [] : getRowIds(products.selectedRows),
					excludedIds: products.selectAllFilter ? getRowIds(products.deselectedRows) : [],
				},
			})
		).then(() => {
			dispatch(getStockTable({ ...getStockFilterOptions() })).then(() => {
				columnCheckDeselectAll();
				setShowSystemStocksPopup(false);
			});
		});
	}

	function SetupBulkImport() {
		//Load departments and subdepartments
		dispatch(getDepartments());
		dispatch(getSubDepartments(undefined));
		dispatch(getSuppliers());
		dispatch(getStockGroups());
		dispatch(getManufacturers());

		//Load suppliers
		//Show the modal window!
		handleChange("bulkEditWizardPopup", true);
	}

	function cleanModel(model: ManagedStockView): ManagedStockView {
		//we'll quickly run through the model to remove any invalid Ids (eg subdepartments that don't exist on this store group).
		//This shouldn't occur though, but just incase

		let temp = { ...model };

		if (model.departmentId != null && !departments.table.some((i) => i.id === model.departmentId)) {
			temp.departmentId = null;
		}

		if (model.subdepartmentId != null && !subDepartments.table.some((i) => i.id === model.subdepartmentId && i.departmentId === model.departmentId)) {
			temp.subdepartmentId = null;
		}

		if (model.manufacturerId != null && !manufacturers.table.some((i) => i.id === model.manufacturerId)) {
			temp.manufacturerId = null;
		}

		if (model.supplierId != null && !suppliers.table.some((i) => i.id === model.supplierId)) {
			temp.supplierId = null;
		}

		if (model.stockGroupIds != null) {
			temp.stockGroupIds = model.stockGroupIds.filter((i) => stockGroups.table.some((j) => j.id === i));
		}

		return temp;
	}

	function WholeTableValidation(rows: CommonColumnProperties[]): CommonColumnProperties[] {
		let temp: CommonColumnProperties[] = [...rows];
		let IsValid = (text: any) => text !== undefined && text !== null && text !== "";

		const apnSet = new Set<string>();
		const pdeSet = new Set<string>();

		rows.forEach((row) => {
			if (row.hasError === undefined || row.hasError === false) {
				//don"t bother validating bad rows
				let errors: string[] = [];

				//Duplicate apns and pdes
				if (IsValid(row.__apn)) {
					if (apnSet.has(row.__apn)) {
						errors.push("Duplicate APN");
					} else {
						apnSet.add(row.__apn);
					}
				}

				if (IsValid(row.__pde) && IsValid(row.__supplier)) {
					if (pdeSet.has(row.__pde + "|" + row.__supplier)) {
						errors.push("Duplicate PDE");
					} else {
						pdeSet.add(row.__pde + "|" + row.__supplier);
					}
				}

				//Department
				if (IsValid(row.department)) {
					row.departmentId = departments.table.find((i) => i.name?.toLocaleLowerCase() === row.department?.toLocaleLowerCase())?.id;
					if (row.departmentId === undefined) {
						errors.push("Department not found");
					}
				}
				//Subdepartment
				if (IsValid(row.subdepartment)) {
					if (row.departmentId !== undefined) {
						//Must be set before we try figure out the subdepartment
						row.subDepartmentId = subDepartments.table.find((i) => i.departmentId === row.departmentId && i.name?.toLocaleLowerCase() === row.subdepartment?.toLocaleLowerCase())?.id;
						if (row.subDepartmentId === undefined) {
							errors.push("Subdepartment not found");
						}
					} else {
						errors.push("Department missing for subdepartment matching");
					}
				}

				//Supplier
				if (IsValid(row.supplier)) {
					row.supplierId = suppliers.table.find((i) => i.name?.toLocaleLowerCase() === row.supplier?.toLocaleLowerCase())?.id;
					if (row.supplierId === undefined) {
						errors.push("Supplier not found");
					}
				}
				if (IsValid(row.manufacturer)) {
					row.manufacturerId = manufacturers.table.find((i) => i.name?.toLocaleLowerCase() === row.manufacturer?.toLocaleLowerCase())?.id;
					if (row.manufacturerId === undefined) {
						errors.push("Manufacturer not found");
					}
				}

				if (IsValid(row.stockGroup)) {
					const matchingStockGroup = stockGroups.table.find((i) => i.name?.toLocaleLowerCase() === row.stockGroup?.toLocaleLowerCase());
					if (matchingStockGroup != null) {
						row.stockGroupIds = [matchingStockGroup.id];
					}

					if (row.stockGroupIds === undefined) {
						errors.push("Stock group not found");
					}
				}

				if (IsValid(row.maxStockLevel) && IsValid(row.minStockLevel)) {
					if (Number.parseInt(row.maxStockLevel) < Number.parseInt(row.minStockLevel)) {
						errors.push("Max stock level must be higher than min stock level");
					}
				}
				if (errors.length > 0) {
					row.hasError = true;
					row.validationState = ValidationState.Invalid;
					row.message = [...(row.message == null || row.message == "" ? [] : [row.message]), ...errors].join(", ");
				}
			}
		});
		return temp;
	}

	async function bulkImportUpdateAsync(rows: CommonColumnProperties[]): Promise<any[]> {
		const toImportFromSystem = rows.filter((i) => i.matchedAgainst === "System"); //This is importing from system lines
		const toCreateOrUpdate = rows.filter((i) => i.matchedAgainst === "Managed" || i.matchedAgainst === "None"); //Updating existing managed lines

		setImportPercentage(0);

		const importedFromSystem = await importStockAsync(toImportFromSystem);

		setImportPercentage(33.34);
		const createdOrUpdated = await bulkImportManagedStocks(toCreateOrUpdate);

		return [...importedFromSystem, ...createdOrUpdated];
	}

	const terminateWorkersRef = useRef<Function>();

	const batchSize = 5000;

	async function bulkImportManagedStocks(rows: CommonColumnProperties[]): Promise<any[]> {
		const webWorkerData: StockDetailImportWorkerData = {
			data: rows,
			createCustomStock: createStockFromNewLines,
			updateName: updateStockName,
		};

		const errors: Map<string, string> = new Map<string, string>();

		const { runWorkers, terminateWorkers } = runBatchWorkers<StockDetailImportWorkerData, CommonColumnProperties, StockDetailImportWorkerResponseData>({
			workerData: webWorkerData,
			createWorker: () => new Worker(new URL("../Managed/stockDetailsImportWorker.ts", import.meta.url)),
			onWorkerResponse: async (event, responseContext, httpErrors, reject) => {
				if (!responseContext.response.data) {
					if (httpErrors) {
						const combinedError = httpErrors.join(", ");
						// Now using this combined error, construct a { stockName, error } object for each stock and push it onto the errors array
						responseContext.originalBatchData.forEach((r: any) => {
							const errorEntry = errors.get(r.name);
							errors.set(r.name ?? "", errorEntry ? errorEntry + ", " + combinedError : combinedError);
						});
					}
				} else {
					// Add to errors for each item in response.data.nonMatchingStocks in the structure { stockName: s, errorMsg: "Could not find a match for this stock" }
					responseContext.response.data.nonMatchingStocks.forEach((nms) => {
						const errorEntry = errors.get(nms.name);
						errors.set(nms.name, errorEntry ? errorEntry + ", " + "Could not match stock" : "Could not match stock");
					});
				}

				setImportPercentage((prev) => Math.min(50, prev + (1 / responseContext.numBatches) * 16.67));
			},
			onWorkersSuccess: async (workerResponses) => {
				// Only create and update stocks if all workers completed successfully.
				const updateStocks = workerResponses.flatMap((r) => r.data?.stocksToUpdate);
				const createStocks = workerResponses.flatMap((r) => r.data?.stocksToCreate);

				//for all update, just make sure they're all valid
				const updatedStocks = updateStocks.map((i) => cleanModel(i));

				setImportLabel("Creating stocks...");
				setImportPercentage(50);
				const loops = Math.ceil(createStocks.length / batchSize) + Math.ceil(updatedStocks.length / batchSize);
				// TODO: We should move this into web workers to proper batch processing and pooling
				// Dispatch create stock call in batches of 1000
				for (let i = 0; i < createStocks.length; i += batchSize) {
					const createStocksBatch = createStocks.slice(i, Math.min(i + batchSize, createStocks.length));
					const createResponse = await dispatch(createStock(createStocksBatch));
					if (createResponse.errors.length > 0) {
						createResponse.errors.forEach((w) => {
							errors.set(createStocks[w.index].name, w.message);
						});
					}

					setImportPercentage((prev) => Math.min(75, prev + (1 / loops) * 25));
				}

				// TODO: We should move this into web workers to proper batch processing and pooling
				// Dispatch update stock call in batches of 1000
				setImportLabel("Updating stocks...");
				setImportPercentage(75);
				for (let i = 0; i < updatedStocks.length; i += batchSize) {
					const updatedStocksBatch = updatedStocks.slice(i, Math.min(i + batchSize, updatedStocks.length));
					const updateResponse = await dispatch(updateMultipleStocks(updatedStocksBatch));
					if (updateResponse.errors.length > 0) {
						updateResponse.errors.forEach((w) => {
							errors.set(updatedStocks[w.index].name, w.message);
						});
					}
					setImportPercentage((prev) => Math.min(100, prev + (1 / loops) * 25));
				}

				await dispatch(getStockTable({ ...getStockFilterOptions() }));
				setImportPercentage(0); //Reset the import percentage
			},
			onWorkersFinished: async (workerResponses, errorData) => {
				// If runWorkers is resolved and we have errors, send all stocks to post-import screen with the error (if applicable)
				const updateStocks = workerResponses.flatMap((r) => r.data?.stocksToUpdate);
				const createStocks = workerResponses.flatMap((r) => r.data?.stocksToCreate);
				const allNames = [...createStocks.map((s) => s.name ?? "No name"), ...updateStocks.map((s) => s.name ?? "No name"), ...errorData.unprocessedData.map((s) => s.name ?? "No name")];

				if (allNames.length > 0) {
					allNames.forEach((name) => {
						const errorEntry = errors.get(name);
						const errorMessage = errorData.error?.message;
						if (errorMessage != null) errors.set(name, errorEntry ? errorEntry + ", " + errorMessage : errorMessage);
					});
				}
			},
		});

		terminateWorkersRef.current = terminateWorkers;
		await runWorkers;

		const returnErrors: { stockName: string; error: string }[] = [];

		errors.forEach((value: string, key: string) => {
			returnErrors.push({
				stockName: key,
				error: value,
			});
		});

		return [...returnErrors];
	}

	async function importStockAsync(rows: any[]): Promise<any[]> {
		if (rows.length === 0) return [];

		const webWorkerData: SystemStocksImportWorkerData = {
			data: rows,
		};

		const errors: Map<string, string> = new Map<string, string>();

		const { runWorkers, terminateWorkers } = runBatchWorkers<SystemStocksImportWorkerData, CommonColumnProperties, SystemStocksImportWorkerResponseData>({
			workerData: webWorkerData,
			createWorker: () => new Worker(new URL("../Managed/systemStocksImportWorker.ts", import.meta.url)),
			onWorkerResponse: async (event, responseContext, httpErrors, reject) => {
				if (!responseContext.response.data) {
					if (httpErrors) {
						const combinedError = httpErrors.join(", ");
						// Now using this combined error, construct a { stockName, error } object for each stock and push it onto the errors array
						responseContext.originalBatchData.forEach((r: any) => {
							const errorEntry = errors.get(r.name);
							errors.set(r.name ?? "", errorEntry ? errorEntry + ", " + combinedError : combinedError);
						});
					}
				}

				setImportPercentage((prev) => Math.min(16.67, prev + (1 / responseContext.numBatches) * 16.67));
			},
			onWorkersSuccess: async (workerResponses) => {
				const productImports = workerResponses.filter((r) => r.data != null).flatMap((r) => r.data!.productImports);

				setImportPercentage(16.67);
				const loops = Math.ceil(productImports.length / batchSize);
				// TODO: We should move this into web workers to proper batch processing and pooling
				// Dispatch update stock call in batches of 1000
				for (let i = 0; i < productImports.length; i += batchSize) {
					const updatedStocksBatch = productImports.slice(i, Math.min(i + batchSize, productImports.length));
					const importResponse = await dispatch(importFromProductsWithData(updatedStocksBatch));
					if (importResponse.errors.length > 0) {
						importResponse.errors.forEach((w) => {
							errors.set(productImports[w.index].name ?? "No Name", w.message);
						});
					}
					setImportPercentage((prev) => Math.min(33.34, prev + (1 / loops) * 16.67));
				}

				await getStockTable({ ...getStockFilterOptions() });
				setImportLabel(undefined);
			},
			onWorkersFinished: (workerResponses, errorData) => {
				// If runWorkers is resolved and we have errors, send all stocks to post-import screen with the error (if applicable)
				const productImports = workerResponses.filter((r) => r.data != null).flatMap((r) => r.data!.productImports);
				const allNames = [...productImports.map((p) => p.name ?? "No name"), ...errorData.unprocessedData.map((d) => d.name ?? "No name")];

				if (allNames.length > 0) {
					allNames.forEach((name) => {
						const errorEntry = errors.get(name);
						const errorMessage = errorData.error?.message;
						if (errorMessage != null) errors.set(name, errorEntry ? errorEntry + ", " + errorMessage : errorMessage);
					});
				}
			},
		});

		terminateWorkersRef.current = terminateWorkers;
		setImportLabel("Importing stocks...");
		await runWorkers;

		const returnErrors: { stockName: string; error: string }[] = [];

		errors.forEach((value: string, key: string) => {
			returnErrors.push({
				stockName: key,
				error: value,
			});
		});

		return [...returnErrors];
	}

	const CustomSearchButton = () => {
		return (
			<>
				<Dropdown.Item onClick={() => handleChange("searchPdes", !stocks.searchPdes)}>
					<Form.Check type="checkbox" label="PDE" checked={stocks.searchPdes} onChange={() => handleChange("searchPdes", !stocks.searchPdes)} />
				</Dropdown.Item>
				<Dropdown.Item onClick={() => handleChange("searchBarcodes", !stocks.searchBarcodes)}>
					<Form.Check type="checkbox" label="APN" checked={stocks.searchBarcodes} onChange={() => handleChange("searchBarcodes", !stocks.searchBarcodes)} />
				</Dropdown.Item>
			</>
		);
	};

	const [openedFromDropdown, setOpenedFromDropdown] = useState<boolean>(false);
	const [createStockFromNewLines, setCreateStockFromNewLines] = useState<boolean>(openedFromDropdown);
	const [updateStockName, setUpdateStockName] = useState<boolean>(false);
	const [selectedStockGroupIds, setSelectedStockGroupIds] = useState<number[]>([]);

	const additionalOptions: ImportWizardSetting[] = [
		{
			key: "stockGroup",
			element: (
				<ZMultiSelectDropdown
					id="stockGroupsImportOption"
					canClear
					onAddNewOption={async (x) => {
						dispatch(
							createStockGroup({
								name: x,
								id: -1,
								modelId: "",
								locked: false,
							})
						).then((r) => {
							dispatch(getStockGroups());
							setSelectedStockGroupIds((prev) => {
								return [...prev, r.data[0]];
							});
						});
					}}
					maxShownSelectedOptions={2}
					placeholder="Select stock groups"
					label={<ZLabel>Stock Groups</ZLabel>}
					options={stockGroups.table}
					values={stockGroups.table.filter((sg) => selectedStockGroupIds.some((v) => v === sg.id))}
					value={(opt) => opt.id}
					disabled={() => false}
					display={(opt) => opt.name ?? "Unknown"}
					onSelectionChange={(stockGroups) => {
						setSelectedStockGroupIds(stockGroups.map((sg) => sg.id) ?? []);
					}}
					hasSearch
					searchPlaceholder="Type to search or add new stock group"
				/>
			),
		},
		{
			key: "createStockFromNewLines",
			element: (
				<ZCheckboxButton
					label="Create stock from new lines"
					key="createStockFromNewLines"
					type="checkbox"
					checked={createStockFromNewLines}
					onClick={() => setCreateStockFromNewLines(!createStockFromNewLines)}
				/>
			),
		},
		{
			key: "updateStockName",
			element: <ZCheckboxButton label="Update stock name" key="updateStockName" type="checkbox" checked={updateStockName} onClick={() => setUpdateStockName(!updateStockName)} />,
		},
	];

	function onSettingsSubmit() {
		let temp = [...importWizardExtraMatches];

		if (createStockFromNewLines || updateStockName) {
			temp.forEach((col) => {
				if (col.key === "name") {
					col.required = true;
				}
			});
		} else {
			temp.forEach((col) => {
				if (col.key === "name") {
					col.required = false;
				}
			});
		}

		importWizardRef.current?.UpdateColumns(temp);
	}

	function onSettingsReset() {
		setCreateStockFromNewLines(openedFromDropdown);
		setUpdateStockName(false);
		setSelectedStockGroupIds([]);
		setImportPercentage(0);
		setImportLabel(undefined);
	}

	enum StockState {
		Create = 0,
		Update = 1,
		Import = 2,
	}

	function handleStockDetailsImportPostProcess(rows: CommonColumnProperties[]): CommonColumnProperties[] {
		// Add the selected stock group ids from the additional settings page to the stocks that are to be created/updated during import
		const stockIdToRow = new Map<string, number>();

		const replaceFirstErrorMessage = (message: string, replaceWith: string) => {
			if (message.split(", ").length > 1) {
				return [replaceWith, ...message.split(", ").slice(1)].join(", ");
			} else {
				return replaceWith;
			}
		};

		//To make sure we don't try to import the same system stock twice
		let importIds = new Set<number>();

		let isNameValid = (text: any) => text !== undefined && text !== null && text !== "";
		rows.forEach((row, index, arr) => {
			if (row.processed === ProcessingState.Failed && row.result !== "No Match") {
				return row;
			}

			let stockState = StockState.Update;
			if (row.matchedAgainst === "Managed") {
				stockState = StockState.Update;
			} else if (row.matchedAgainst === "System") {
				stockState = StockState.Import;
			} else {
				stockState = StockState.Create;
			}
			// If we are updating, we need to check if there are multiple rows matching to the same stock.
			// If there are, we need to invalidate them
			if (stockState === StockState.Update) {
				// We will create a dictionary here to map stock ids to a row that's matched to it
				// Now, if the entry doesn't exist, we will add it to the map
				// if it does, we will invalidate both the current row and the row that's already in the map
				// With a message saying "Duplicate match to <Stock Name>"
				// If the row doesn't have a stockId, we will skip it
				if (row.id == null) {
					return row;
				}

				if (row.id != "0" && row.id != "" && stockIdToRow.has(row.id)) {
					const existingRowIx = stockIdToRow.get(row.id);
					if (existingRowIx != null) {
						// We always return Matched to <Stock Name> as the first message, so if there's a duplicate match we can replace this message with the new one and keep the other messages
						const existingRowMessage = rows[existingRowIx].message ?? "";
						rows[existingRowIx] = {
							...rows[existingRowIx],
							hasError: true,
							processed: ProcessingState.Failed,
							message: replaceFirstErrorMessage(existingRowMessage, `Duplicate match to ${row.__name}`),
						};

						row.hasError = true;
						row.message = replaceFirstErrorMessage(row.message ?? "", `Duplicate match to ${row.__name}`);
						row.processed = ProcessingState.Failed;
					}
				} else {
					stockIdToRow.set(row.id, index);
				}
			} else {
				// Matched against System, if the setting to create stock from new lines isn't enabled, this row is invalid
				if (!createStockFromNewLines) {
					row.processed = ProcessingState.Failed;
					row.hasError = true;
					row.message = `${row.matchedAgainst === "System" ? "System" : "No"} Match - Create lines option not enabled`;
				}
			}

			const rowIdNumber = parseInt(row.id ?? "");

			if (stockState === StockState.Import && !row.hasError && Number.isInteger(rowIdNumber) && importIds.has(rowIdNumber)) {
				row.processed = ProcessingState.Failed;
				row.hasError = true;
				row.message = "Duplicate master product - trying to import the same line twice";
			} else if (Number.isInteger(rowIdNumber)) {
				importIds.add(rowIdNumber);
			}

			row = {
				...row,
				stockGroupIds: [...(row.stockGroupIds != null ? row.stockGroupIds : []), ...selectedStockGroupIds].filter(onlyUnique),
				stockState: stockState,
			};

			rows[index] = { ...row };

			// We only want to do this post-processing if we are creating stock from new (aka non-matching) lines.
			if (createStockFromNewLines) {
				let newRow = { ...row };
				if (row.matchedAgainst === "None") {
					// For each non-matched stock, we need to do a few things:
					// 1. Validate that name is not empty => If it is, skip it (it's already failed).
					if (!isNameValid(newRow.name)) {
						newRow.hasError = true;
						let errors = ["Name is required for new custom stocks"];

						if (newRow.message) {
							errors = [newRow.message, ...errors];
						}

						newRow.message = errors.join(",");
						rows[index] = { ...newRow };
					}

					if (newRow.costEx == null || newRow.costEx == "") {
						newRow.costEx = 0;
					}

					// 2. If the row is missing required fields such as min order qty, set a default value here.
					newRow = {
						minOrderQty: 1,
						cartonSize: 1,
						minStockLevel: 1,
						active: true,
						ethical: false,
						gstCode: "N",
						...newRow,
						costEx: isNaN(parseFloat(newRow.costEx)) ? 0 : parseFloat(newRow.costEx),
						baseSellPrice: isNaN(parseFloat(newRow.baseSellPrice)) ? 0 : parseFloat(newRow.baseSellPrice),
						// 3. Set ProcessingState to Done
						message: "",
						processed: ProcessingState.Done,
						stockState: StockState.Create,
					};
				}

				rows[index] = { ...newRow };
				// This can then get forwarded to the import stage where it will be checked.
			}
		});

		return rows;
	}

	const agGridColumns: (ColDef<ManagedStockView> | ColGroupDef<ManagedStockView>)[] | null = [
		{
			headerName: "Name",
			field: "name",
			flex: 1,
			checkboxSelection: true,
			headerCheckboxSelection: true,
			editable: true,
			cellEditor: PriceCellEditor,
			filter: "agTextColumnFilter",
			cellRenderer: (params: CustomCellRendererProps) => {
				return user.permissions.managedStock.canUpdate ? <Link to={`/app/stock/${params.data.id}`}>{params.value}</Link> : <>{params.value}</>;
			},
		},
		{
			headerName: "Ethical",
			field: "ethical",
			cellDataType: "boolean",
			filter: true,
			editable: true,
			// filter: 'agSetColumnFilter',
			// filterParams: {
			// 	values: ["Yes", "No"],
			// },
			cellRenderer: "agTextCellRenderer",
			valueFormatter: (p) => (p.value ? "Yes" : "No"),
		},
		{
			headerName: "Active",
			field: "active",
			cellDataType: "boolean",
			filter: true,
			// filter: 'agSetColumnFilter',
			// filterParams: {
			// 	values: ["Yes", "No"],
			// },
			cellRenderer: "agTextCellRenderer",
			valueFormatter: (p) => (p.value ? "Yes" : "No"),
		},
		{
			headerName: "Z ID",
			field: "productNo",
			filter: "agNumberColumnFilter",
			cellDataType: "number",
		},
		{
			headerName: "Department",
			field: "departmentId",
			initialHide: true,
			valueGetter: (params) => departments.table.find((d) => d.id === params.data!.departmentId)?.name,
		},
	];

	const gridRef = useRef<AgGridReact>(null);

	const getServerSideDatasource: () => IServerSideDatasource = () => {
		return {
			getRows: async (params) => {
				console.log("[Datasource] - rows requested by grid: ", params.request);

				const respopnse = await dispatch(
					getStockTable({
						keyword: gridSearch.current?.searchState?.keyword,
						contains: gridSearch.current?.searchState?.contains,
						offset: params.request.startRow,
						limit: params.request.endRow != undefined && params.request.startRow != undefined ? params.request.endRow - params.request.startRow : 100,
						sortModels: params.request.sortModel,
						...gridSearch.current?.filterState,
					})
				);

				params.success({
					rowData: respopnse.data,
					rowCount: respopnse.totalRows,
				});
			},
		};
	};

	const onGridReady = useCallback((params: GridReadyEvent) => {
		var datasource = getServerSideDatasource();
		// register the datasource with the grid
		params.api!.setGridOption("serverSideDatasource", datasource);
		applyGridState();
	}, []);

	const [selectedDepartment, setSelectedDepartment] = useState<ManagedDepartment | undefined>(undefined);
	const gridSearch = useRef<{ searchState?: SearchState; filterState?: FilterState }>({});
	const toolbarRef = useRef<AgGridToolbarRef>(null);
	const [serverSideSelectionState, setServerSideSelectionState] = useState<{ selectAll: boolean; toggledNodes: ManagedStockView[] }>({ selectAll: false, toggledNodes: [] });

	const [temp, setTemp] = useState<{ settings: GridSettings }>();

	// Things we'd ideally like to save for custom templates:
	// - Selected columns
	// - Column visibility
	// - Sort order
	// - Column filter state
	// - Toolbar filter state
	// - Search state?
	// - Name & Description of report
	// - Row grouping

	type GridSettings = {
		columnState: ColumnState[];
		filterState: FilterModel;
		toolbarFilterState: Map<string, any>;
		advancedFilterState: AdvancedFilterModel | null;
		searchState: SearchState;
		name: string;
		description: string;
	};

	// Fetch the report template by id and apply the settings to the AgGrid ref
	function applyGridState() {
		if (temp) {
			console.log("Applying grid settings: ", temp.settings);
			// Need to take in both column states and filter state and apply it to the grid on
			gridRef.current?.api.applyColumnState({ state: temp.settings.columnState });
			gridRef.current?.api.setFilterModel(temp.settings.filterState ?? null);
			gridRef.current?.api.setAdvancedFilterModel(temp.settings.advancedFilterState ?? null);
			gridSearch.current = { searchState: temp.settings.searchState, filterState: temp.settings.filterState };

			toolbarRef.current?.loadFilters(temp.settings.toolbarFilterState);
		}
		// We need to do something like this to apply state to the grid on load.
		// dispatch(getReportTemplate({ id: reportId })).then((settings: GridSettings) => {
		// 	// Need to take in both column states and filter state and apply it to the grid on
		// 	gridRef.current?.api.applyColumnState({ state: settings.columnState });
		// 	gridRef.current?.api.setFilterModel(settings.filterState ?? null);
		// 	gridRef.current?.api.setAdvancedFilterModel(settings.advancedFilterState ?? null);
		// 	gridSearch.current = { searchState: settings.searchState, filterState: settings.filterState };
		// });
	}

	// Save the report template to the database by collating the current state of the grid and sending it with the report id
	function saveGridState() {
		const gridSettings: GridSettings = {
			columnState: gridRef.current!.api.getColumnState() ?? [],
			filterState: gridRef.current!.api.getFilterModel(),
			advancedFilterState: gridRef.current!.api.getAdvancedFilterModel(),
			searchState: gridSearch.current!.searchState ?? { keyword: "", contains: true },
			toolbarFilterState: toolbarRef.current!.saveFilters() ?? {},
			name: "TEST",
			description: "Test description",
		};

		console.log("Saving grid settings: ", gridSettings);

		setTemp({ settings: gridSettings });
		// TODO: Instead of setting temp, we will do an API call here to save it to the DB. We also need to give it an ID to save it under for custom templates

		// dispatch(saveReportTemplate({ id: reportId, settings: gridSettings }));
	}

	return (
		<>
			<Title
				pageTitle="Managed Stocks"
				appendTitle={
					global.numStockConflicts > 0 ? (
						<ZFeatureFlag flag="ZFreedom.ManagedStock.StockConflicts" value={true}>
							<Z.Tooltip text={`You have ${global.numStockConflicts} stock conflicts, click here to resolve them`}>
								<Z.Button
									variant={`${global.numStockConflicts > 20 ? "danger" : "warning"}`}
									className="cta-button py-0 px-1"
									icon={ZIconType.Alert}
									onClick={() => navigate("/app/stock-conflicts")}
									buttonText={`${global.numStockConflicts < 100 ? global.numStockConflicts : "99+"}`}
								/>
							</Z.Tooltip>
						</ZFeatureFlag>
					) : (
						<></>
					)
				}
			/>

			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							{/* <AgGridToolbar search={true}
								ref={toolbarRef}
								className="my-2 px-1"
								handleSubmit={(s,f) => {
									gridSearch.current = {searchState: s, filterState: f};
									gridRef.current?.api.refreshServerSide()
								}}
								filters={[
									createSingleSelectFilter<ManagedSupplier>({
										label: "Supplier",
										filterName: "supplierIds",
										options: suppliers.table,
										value: (option) => [option.id],
										display: (option) => option.name,
										hasNoneOption: true,
									}),
									createSingleSelectFilter<ManagedDepartment>({
										label: "Department",
										filterName: "departmentIds",
										options: departments.table,
										value: (option) => [option.id],
										display: (option) => option.name,
										onFilterChange: (e) => setSelectedDepartment(e),
										hasNoneOption: true,
									}),
									createSingleSelectFilter<ManagedSubdepartment>({
										label: "Sub Department",
										filterName: "subdepartmentIds",
										options: subDepartments.table.filter( i=> i.departmentId === selectedDepartment?.id),
										value: (option) => [option.id],
										display: (option) => option.name,
										hasNoneOption: true,
									}),
									createSingleSelectFilter<ManagedStockGroup>({
										label: "Stock Group",
										filterName: "stockGroupIds",
										options: stockGroups.table,
										value: (option) => [option.id],
										display: (option) => option.name,
										hasNoneOption: true,
									}),
									createBooleanSelectFilter({
										label: "Active",
										filterName: "active",
										hasNoneOption: true,
									}),
									createBooleanSelectFilter({
										label: "Ethical",
										filterName: "ethical",
										hasNoneOption: true,
									}),
									createSingleSelectFilter<string>({
										label: "Schedule",
										filterName: "schedule",
										options: ScheduleCodes,
										value: (option) => option,
										display: (option) => option,
										hasNoneOption: true,
									}),
									createBooleanSelectFilter({
										label: "PBS",
										filterName: "pbs",
										hasNoneOption: true,
									}),
								]}
								searchProps={
									{totalRows: stocks.totalRows, placeholder: "by Name / PDE / APN"}
								}
								children={(<div className="d-inline-flex gap-1">
									<Z.Button icon={ZIconType.Add} buttonSize="md" onClick={() => setShowSystemStocksPopup(true)} disabled={!user.permissions.managedStock.canCreate} />
									<Z.Button icon={ZIconType.Edit} buttonSize="md" onClick={() => {
										if (gridRef.current?.api && serverSideSelectionState.toggledNodes.length > 1) {
											handleChange("bulkEditPopup", true)
										} else {
											navigate(`/app/stock/${serverSideSelectionState.toggledNodes[0].id}`);
										}
									} } disabled={!user.permissions.managedStock.canUpdate || !serverSideSelectionState.selectAll || serverSideSelectionState.toggledNodes.length === 0} />
									<Z.Button icon={ZIconType.Delete} buttonSize="md" onClick={() => setShowSystemStocksPopup(true)} disabled={!user.permissions.managedStock.canDelete} />
									<Z.Button icon={ZIconType.Refresh} buttonSize="md" onClick={() => setShowSystemStocksPopup(true)} disabled={!user.permissions.managedStock.canView} />
									<Z.Button icon={ZIconType.Merge} buttonSize="md" onClick={() => setShowSystemStocksPopup(true)} disabled={!user.permissions.managedStock.canUpdate} />
									<Z.Button icon={ZIconType.Sync} buttonSize="md" onClick={() => setShowSystemStocksPopup(true)} />
									<Z.Button icon={ZIconType.Upload} buttonSize="md" onClick={() => setShowSystemStocksPopup(true)} disabled={!user.permissions.managedStock.canCreate || !user.permissions.managedStock.canUpdate} />
									<ZButton icon={ZIconType.Save} buttonSize='md' buttonText="Save" iconSize='18px' variant="primary" onClick={() => {
										console.log(gridRef.current?.api.getFilterModel());
										console.log(gridRef.current?.api.getState());
										console.log(gridRef.current?.context);
										console.log(gridRef.current?.api.getAdvancedFilterModel());
										// setGridState(gridRef.current?.api.getColumnState());
										saveGridState();
									}}/>
									<ZButton icon={ZIconType.Upload} buttonSize='md' buttonText="Load" iconSize='18px' variant="light" onClick={() => {
										console.log(gridRef.current?.api.getFilterModel());
										console.log(gridRef.current?.state);
										console.log(gridRef.current?.context);
										console.log(gridRef.current?.api.getAdvancedFilterModel());
										// gridRef.current?.api.applyColumnState({state: gridState});
										applyGridState();
									}}/>
								</div>
									
								)}
							/> */}
							<Row>
								<div className="page-container">
									<ManagedStockMerge
										loadStocks={false} //TODO: We can remove this property once we tear redux out of the managed stock page
										show={stocks.manualMergePopup}
										stockIds={getRowIds(stocks.selectedRows)}
										handleClose={() => handleChange("manualMergePopup", false)}
										afterMerge={(mergedStockId: number) => {
											handleChange("manualMergePopup", false);
											navigate(`/app/stock/${mergedStockId}?openSync=true`);
										}}
									/>
									<SystemStocksPopup
										show={showSystemStocksPopup}
										onClose={() => {
											setShowSystemStocksPopup(false);
										}}
										onSave={saveSelectedSystemStocks}
										canSelectAll={true}
									/>
									<ManagedStockSync
										isOpen={syncOpen}
										onClose={() => setSyncOpen(false)}
										counter={counterSelectedRows}
										filter={{
											...getStockFilterOptions(),
											ids: stocks.selectAllFilter ? [] : getRowIds(stocks.selectedRows),
											excludedIds: stocks.selectAllFilter ? getRowIds(stocks.deselectedRows) : [],
										}}
									/>
									<BulkEdit show={stocks.bulkEditPopup} handleClose={() => handleChange("bulkEditPopup", false)} />

									{/* Bulk Import/Update Wizard */}
									<ImportWizard
										ref={importWizardRef}
										title="Stock Details"
										display={stocks.bulkEditWizardPopup}
										handleClose={() => {
											if (terminateWorkersRef.current) terminateWorkersRef.current();
											handleChange("bulkEditWizardPopup", false);
										}}
										importPercentage={importPercentage}
										importLabel={importLabel}
										importAsync={(i) => bulkImportUpdateAsync(i)}
										postProcessStep={(r) => handleStockDetailsImportPostProcess(r)}
										customColumns={importWizardExtraMatches}
										validateTable={(rows) => WholeTableValidation(rows)}
										matchAgainst="Both"
										finish={() => handleChange("bulkEditWizardPopup", false)}
										onSettingsSubmit={onSettingsSubmit}
										onSettingsReset={onSettingsReset}
										showAllImportByColumns
										matchedColumnOverride={{
											headerName: "Status",
											pinned: true,
											sortable: true,
											field: "stockState",
											maxWidth: 500,
											cellRendererSelector: (params) => {
												const issueArray = params.data.message.split(", ");
												const cellRendererParams = {
													decorator:
														params.data.processed === ProcessingState.Failed ? (
															<ZTooltip
																tooltipClassName="tooltip-text-start"
																text={`This stock has issues${issueArray.length > 0 ? ": \n• " + issueArray.join("\n• ") : ""}`}
															>
																<ZIcon iconClassName="me-1" icon={ZIconType.Alert} variant="warning" />
															</ZTooltip>
														) : params.value === StockState.Create ? (
															<ZTooltip text="This stock will be created">
																<ZIcon icon={ZIconType.Add} variant="primary" />
															</ZTooltip>
														) : params.value === StockState.Import ? (
															<ZTooltip text="This stock will be imported">
																<ZIcon icon={ZIconType.Upload} variant="primary" />
															</ZTooltip>
														) : (
															<ZTooltip text="This stock will be updated">
																<ZIcon icon={ZIconType.Check} variant="primary" />
															</ZTooltip>
														),
													decoratorPosition: "start",
													align: "left",
													children: (
														<>{params.data.processed === ProcessingState.Failed ? <div className="text-truncate overflow-hidden">{params.data.message}</div> : <></>}</>
													),
												};
												return { component: "baseCellRenderer", params: cellRendererParams };
											},
										}}
										additionalSettings={additionalOptions}
									/>
									{/* 
									<AgGridReact<ManagedStockView>
										ref={gridRef}
										columnDefs={agGridColumns}
										rowModelType="serverSide"
										pagination={true}
										paginationPageSize={20}
										getRowId={(p) => p.data.id.toString()}
										// enableAdvancedFilter
										cacheBlockSize={100}
										onGridReady={onGridReady}
										containerStyle={{ height: "800px", width: "100%" }}
										className="ag-theme-quartz ag-theme-custom w-100"
										reactiveCustomComponents
										rowSelection="multiple"
										suppressRowClickSelection={true}
										suppressCellFocus={true}
										readOnlyEdit // This is needed because we store the data in redux, so the grid cannot update the state as the redux data is immutable, so we have to handle the updates ourselves
										stopEditingWhenCellsLoseFocus
										onCellEditRequest={(e) => console.log("cell edit request", e)}
										onSelectionChanged={(e) => {
											const selection = gridRef.current?.api.getServerSideSelectionState();
											const nodes = selection?.toggledNodes?.map(t => gridRef.current?.api.getRowNode(t.toString())?.data);

											//@ts-ignore
											setServerSideSelectionState(selection && nodes ? {selectAll: selection.selectAll, toggledNodes: nodes} : {selectAll: false, toggledNodes: []});
										}}
									/> */}

									<ProTable
										data={stocks}
										pageSize={100}
										isSortable={true}
										pagination={true}
										table={stocks.table}
										columns={columns}
										loading={stocks.loading}
										updateTableRows={updateTableRows}
										// Search Props
										isSearchable={true}
										searchPlaceHolder={`by Name ${stocks.searchPdes ? "/ PDE" : ""} ${stocks.searchBarcodes ? "/ APN" : ""}`}
										onSearchClick={onSearchClick}
										// Toolbar Props
										removeRows={(deleteCallback) => (user.permissions.managedStock.canDelete ? handleDeleteStock(deleteCallback) : undefined)}
										syncOptions={() => setSyncOpen(true)}
										reloadTableRows={() => dispatch(getStockTable({ ...getStockFilterOptions() }))}
										handleSingleEdit={
											user.permissions.managedStock.canUpdate ? () => stocks.selectedRows.length > 0 && navigate(`/app/stock/${getRowIds(stocks.selectedRows).at(0)}`) : undefined
										}
										handleBulkEdit={user.permissions.managedStock.canUpdate ? () => handleChange("bulkEditPopup", true) : undefined}
										importBulkUpdate={
											user.permissions.managedStock.canUpdate
												? () => {
														setOpenedFromDropdown(false);
														SetupBulkImport();
												  }
												: undefined
										}
										merge={{
											handleMerge: () => handleChange("manualMergePopup", true),
											canMerge: () => {
												const ids = getRowIds(stocks.selectedRows);
												const selectedSystemStocks = stocks.table.filter((s) => ids.find((id) => id === s.id) != null && s.productNo != null);
												return selectedSystemStocks.length <= 1;
											},
										}}
										customAddButton={
											<Dropdown>
												<Dropdown.Toggle variant="light" id="dropdown-basic" className="btn-icon custom-btn-icon">
													<ZIcon icon={ZIconType.Add} size="18px" />
												</Dropdown.Toggle>
												<Dropdown.Menu className="w-auto">
													<Dropdown.Item
														className="d-inline-flex align-items-center gap-1"
														onClick={
															user.permissions.managedStock.canCreate
																? () => {
																		setShowSystemStocksPopup(true);
																  }
																: undefined
														}
													>
														<ZIcon icon={ZIconType.System} size="18px" /> Import System Stock
													</Dropdown.Item>
													<Dropdown.Item
														className="d-inline-flex align-items-center gap-1"
														onClick={() => {
															navigate("/app/stock/new");
														}}
													>
														<ZIcon icon={ZIconType.Add} size="18px" /> Create Custom Stock
													</Dropdown.Item>
													<Dropdown.Item
														className="d-inline-flex align-items-center gap-1"
														onClick={
															user.permissions.managedStock.canUpdate
																? () => {
																		setOpenedFromDropdown(true);
																		SetupBulkImport();
																  }
																: undefined
														}
													>
														<ZIcon icon={ZIconType.Upload} size="18px" /> Import Spreadsheet
													</Dropdown.Item>
												</Dropdown.Menu>
											</Dropdown>
										}
										// Select Props
										isSelectable={true}
										selectAllFilter={stocks.selectAllFilter}
										selectedRows={stocks.selectedRows}
										deselectedRows={stocks.deselectedRows}
										selectedRowsUpdate={selectedRowsUpdate}
										deselectedRowsUpdate={deselectedRowsUpdate}
										columnCheckSelectAll={columnCheckSelectAll}
										columnCheckDeselectAll={columnCheckDeselectAll}
										// Extra Components Props
										customSearchButton={<CustomSearchButton />}
										filterComponent={
											<CustomFilter
												data={stocks}
												handleChange={handleFilterChange}
												clearFilters={() => dispatch(clearFilters())}
												applyFilters={() => onSearchClick(stocks.keyword)}
											/>
										}
									/>
								</div>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</>
	);
}

// type AgGridProps<TData = any> = AgGridReactProps & {
// 	onRowSelected?: (row: TData) => void;
// };

// const AgGrid2 = <TData,>(props: AgGridProps<TData>) => {
//     return (
// 	<AgGridReact<TData>
// 		onRowSelected={(e) => {props.onRowSelected && props.onRowSelected(e.data)}}

// 		//Default props
// 		suppressPaginationPanel

// 		//Override
// 		{...props}
// 	/>);
// }

// <AgGrid2 onRowSelected={(e) => {}}/>
